import { ButtonType, ButtonWidget } from '@tableau/widgets';
import React from 'react';

import { Set } from '~commonInfra/utils/Set';

import { Messages } from '../../codegen/Localize';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';

import _ = require('underscore');

import {openReactAlertDialog} from '~commonInfra/dialogs/ReactAlertDialog';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { deepClone } from '~commonInfra/utils/DeepClone';
import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { showAddCredentialAuthModeDialog, showAddCredentialIdpDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { DeprecatedInfoButton } from '~src/connectivity/components/spaces/common/DeprecatedInfoButton';
import { IGetOAuthClientsResult } from '~src/connectivity/serverApi/requests/OAuth/GetOAuthClientsRequest';
import { IOAuthClient } from '~src/connectivity/serverApi/types/OAuth/IOAuthClient';
import { AddKeyPairAuthCredentialAction } from '~src/connectivity/services/AddKeyPairAuthCredentialAction';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';
import { fetchOAuthClients } from '~src/settings/general-settings/components/sections/oauth-clients-registry/OAuthClientsRegistry';

import { openReactActionDialog } from '../../commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { OAuthCredentials } from '../../connectivity/resources/OAuthCredentials';
import { IOAuthConfigInfo, IOAuthCredential, IOAuthCredentialTypeInfo } from '../../connectivity/serverApi/types/OAuth/IOAuthCredential';
import { AddOAuthCredentialAction } from '../../connectivity/services/AddOAuthCredentialAction';
import { OAuthCredentialsService } from '../../connectivity/services/OAuthCredentialsService';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { showDeleteSavedPasswordActionDialog } from '../../siteManagement/services/DeleteMyCredentialsAction';
import { showDeleteOAuthCredentialsDialog } from '../../siteManagement/services/DeleteOAuthCredentialsAction';
import { CredentialTypeFilter } from '../services/filters/CredentialTypeFilter';
import { AddNewOAuthSavedCredentialBody, AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState } from './AddNewOAuthSavedCredentialBody';

const DEEPLINK_ID = 'saved-credentials';

export interface OAuthCredentialViewModel  {
  credential: IOAuthCredential;
  test: {
    // Determine if test button is displayed to customers, as of Q4 2016, we disable them for
    // generic-auth-based WDC credentials. TFS 546010
    enabled?: boolean;
  };
  configLabel?: string;
}

export interface OAuthCredentialsList {
  typeInfo: IOAuthCredentialTypeInfo;
  addEnabled: boolean;
  credentials?: OAuthCredentialViewModel[];
  serviceAccountCredentials?: OAuthCredentialViewModel[];
  oauthConfigIdToCredMap?: {[key: string]: OAuthCredentialViewModel[]};
  oauthConfigIdToLabel?: {[key: string]: string};
}

export type SavedCredentialsForDataSourceSettingProps = {
  serverUser: IServerUserInfo;
}

const createOAuthCredentialLists = (credentials?: IOAuthCredential[], CustomOAuthConfigsGroupsMap?: _.Dictionary<IOAuthClient[]>): OAuthCredentialsList[] => {
  const convertToCredentialViewModel = (credential: IOAuthCredential): OAuthCredentialViewModel => {
    return {credential: credential, test: { enabled: OAuthCredentialsService.supportsTestConnection(credential.type, credential) }};
  };

  const credentialVMGroupsMap = _.groupBy(credentials, c => c.type);
  // Iterate through all OAuth types, if any is enabled, put it in the result
  return _.compact(_.map(OAuthCredentialsService.getAllOAuthCredentialTypes().filter(typeInfo => typeInfo.type !== 'slack'),
    (typeInfo: IOAuthCredentialTypeInfo) => {
      const allCredentials = _.map(credentialVMGroupsMap[typeInfo.type], convertToCredentialViewModel);
      let oauthConfigIdToCredMap: {[key: string]: OAuthCredentialViewModel[]} = {};
      let oauthConfigIdToLabel: {[key: string]: string} = {};
      const oauthConfigList = getFullOAuthConfigsList(typeInfo.oauthConfigInfoList, CustomOAuthConfigsGroupsMap?.[typeInfo.type]);
      if(FeatureFlags.oauthMultipleIdpOnServer && oauthConfigList && oauthConfigList.length > 0) {
        oauthConfigList.forEach(function(item){
          if(item.oauthConfigId)
            oauthConfigIdToCredMap[item.oauthConfigId] = _.filter(allCredentials, c => (!c.credential.serviceAccountCredential &&
              (c.credential.oauthConfigId === item.oauthConfigId || (item.oauthConfigId === 'default' && !c.credential.oauthConfigId))));
            oauthConfigIdToLabel[item.oauthConfigId] = item.configLabel || item.oauthConfigId;
        });
      }
      const oauthCredentialsList: OAuthCredentialsList = {
        typeInfo: typeInfo,
        addEnabled: OAuthCredentials.canAddForConnectionType(typeInfo),
        // Create a list of JUST the oauth credentials, remove the values from the credentials list that
        // are marked as service account credentials.
        credentials: _.filter(allCredentials, c => !c.credential.serviceAccountCredential),
        // Create a list of JUST the service account crednetials, by removing the non-service account credentials.
        serviceAccountCredentials: _.filter(allCredentials, c => c.credential.serviceAccountCredential),
        oauthConfigIdToCredMap: oauthConfigIdToCredMap,
        oauthConfigIdToLabel: oauthConfigIdToLabel
      };

      return (oauthCredentialsList.addEnabled || oauthCredentialsList.credentials.length > 0) ? oauthCredentialsList : null;
    })
  );
};

const getFullOAuthConfigsList = (BuiltInOAuthConfigList?: IOAuthConfigInfo[], ProvidedOAuthConfigList?: IOAuthClient[]) => {
  let oauthConfigList: IOAuthConfigInfo[] = BuiltInOAuthConfigList? deepClone(BuiltInOAuthConfigList) : [];

  ProvidedOAuthConfigList?.forEach((x: IOAuthClient) => {
    oauthConfigList.push({oauthConfigId: x.oauthConfigId, supportsCustomDomain: false, configLabel: x.configLabel});
  });

  return oauthConfigList;
};

const shouldShowAddCredentialIdpDialog = (oauthConfigList: IOAuthConfigInfo[]): boolean => {
    return oauthConfigList.length > 1 || (oauthConfigList.length === 1 && oauthConfigList[0].oauthConfigId !== '' && oauthConfigList[0].oauthConfigId !== 'default');
};

export function SavedCredentialsForDataSourceSetting(props: SavedCredentialsForDataSourceSettingProps): JSX.Element {
  const [credentialsLists, setCredentialsLists] = React.useState(createOAuthCredentialLists() as OAuthCredentialsList[]);
  const CustomOAuthConfigsGroupsMap = React.useRef(null as _.Dictionary<IOAuthClient[]>);

  async function getCredentialsLists() {
    const credentialsListResult: ListResult<IOAuthCredential> = await SliceableHelper.fetchAll(OAuthCredentials.all());
    setCredentialsLists(createOAuthCredentialLists(credentialsListResult.items, CustomOAuthConfigsGroupsMap.current));
  }

  async function getOauthConfigsGroupMap() {
      const oauthConfigsResult: IGetOAuthClientsResult = await fetchOAuthClients();
      CustomOAuthConfigsGroupsMap.current = _.groupBy(oauthConfigsResult.oauthClients.filter(oauthClient => oauthClient.oauthConfigId?.startsWith('custom_')), it => it.type);
  }

  React.useEffect(() => {
    getOauthConfigsGroupMap().then(getCredentialsLists);
  }, []);

  async function OnAddCredentialForSelectedAuthMode(props: any, state: any, updateDialogState: any, type: string){
    if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
        if (type === 'snowflake') {
          if (state.oauthConfigList && FeatureFlags.oauthMultipleIdpOnServer && shouldShowAddCredentialIdpDialog(state.oauthConfigList)) {
            showAddCredentialIdpDialog(type, state.oauthConfigList, OnAddCredentialForSelectedIdp);
          } else {
            getOauthServerRoleAndRedirect(type);
          }
        } else {
          // if this is just a normal OAuth credential we are adding, then call
          // into attemptAuth to start the oauth flow.
          AddOAuthCredentialAction.attemptAuth(type).then(getCredentialsLists);
        }
      } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
        // otherwise if this is a service account file credential we are adding,
        // try and parse the file and then store the new credential.
        await AddServiceAccountFileAction
        .addServiceAccountFile(state.connAuthState.file, type, updateDialogState);
        getCredentialsLists();
      } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_KeyPairAuth()) {
        if (type === 'snowflake') {
          AddKeyPairAuthCredentialAction.addKeyPairAuthCredential(type)
          .then(
            getCredentialsLists
          );
        }
      }
  }

  async function OnAddCredentialForSelectedIdp(props: any, state: any, type: string, oauthConfigList: IOAuthConfigInfo[]){
    const oauthConfigId = state.selectedConnectionAuthMode;

    const supportsCustomDomain = oauthConfigList.find(oauthConfig => oauthConfig.oauthConfigId === oauthConfigId).supportsCustomDomain;
    if(supportsCustomDomain) {
      getOauthServerRoleAndRedirect(type, oauthConfigId);
    } else {
      AddOAuthCredentialAction.attemptAuth(type, null, null, oauthConfigId).then(getCredentialsLists);
    }
  }

  async function OnAddCredentialForServerRole(props: AddNewOAuthSavedCredentialBodyProps, state: AddNewOAuthSavedCredentialBodyState, type: string, oauthConfigId?: string) {
    const server = state.instanceUrl;
    var instanceUrl;
    if (server.startsWith('https://')){
      instanceUrl = server;
    } else if (server.startsWith('http://')){
      instanceUrl = server.replace('http://','https://');
    } else if (server.trim().length !== 0) {
      instanceUrl = 'https://' + server;
    } else {
      instanceUrl = '';
    }
    AddOAuthCredentialAction.attemptAuth(type, instanceUrl, state.role, oauthConfigId, state.server).then(getCredentialsLists);
  }

  const getDefaultInstanceUrl = (type: string) => {
    switch (type) {
      case 'salesforce':
        return Messages.salesforceOAuthInstanceUrlPlaceholder();
      case 'onedrivesharepoint':
        return 'https://login.microsoftonline.com/common';
      default:
        return '';
    }
  };

  const getOauthServerRoleAndRedirect = (type: string, oauthConfigId?: string) => {

    // TODO: TFSID 1256282 change the below to use the REACT dialog for
    // confirm action dialog rather than the legacy angular one... guess this was already kinda done?
    // but we can change this to be more consistent with the new object I created...
    openReactActionDialog<AddNewOAuthSavedCredentialBodyProps, AddNewOAuthSavedCredentialBodyState, any>({
      title:  Messages.addNewSavedCredentialTitle(),
      bodyComponent: AddNewOAuthSavedCredentialBody,
      initialBodyProps: {
        oauthInstanceUrlPlaceholder: getDefaultInstanceUrl(type),
        prompt: Messages.customAuthenticationSettings(),
        supportsOAuthRole: (type === 'snowflake'),
        supportsServer: (type === 'cdata_sharepoint'),
        supportsServerOnly: (type === 'databricks')
      },
      initialDialogState: { instanceUrl: getDefaultInstanceUrl(type), role: '', server: '' },
      actionName: Messages.add(),
      dismissDialogOnActionFailed: true,
      action: (props, state) => {return OnAddCredentialForServerRole(props, state, type, oauthConfigId);},
      actionDisabled: (_bodyProps: AddNewOAuthSavedCredentialBodyProps, dialogState: AddNewOAuthSavedCredentialBodyState) => (_bodyProps.supportsServerOnly && dialogState.server.length === 0) || (!_bodyProps.supportsServerOnly && dialogState.instanceUrl.length === 0),
      dialogWidth: TabDialogWidth.width500px,
  });
};

  const addCredential = async (typeInfo: IOAuthCredentialTypeInfo) => {
    const oauthConfigList = getFullOAuthConfigsList(typeInfo.oauthConfigInfoList, CustomOAuthConfigsGroupsMap.current?.[typeInfo.type]);
    if (typeInfo.type === 'snowflake') {
      if (FeatureFlags.snowflakeKeyPairAuth) {
        showAddCredentialAuthModeDialog(typeInfo.type, OnAddCredentialForSelectedAuthMode, oauthConfigList);
      } else {
        if (FeatureFlags.oauthMultipleIdpOnServer && shouldShowAddCredentialIdpDialog(oauthConfigList)) {
          showAddCredentialIdpDialog(typeInfo.type, oauthConfigList, OnAddCredentialForSelectedIdp);
        } else {
          getOauthServerRoleAndRedirect(typeInfo.type);
        }
      }
    } else if (typeInfo.type === 'bigquery' || typeInfo.type === 'googlebigquery') {
      // TFS1243460 For GBQ, we now support Service Account file credentials AND OAuth, so the user must pick the mode.
      showAddCredentialAuthModeDialog(typeInfo.type, OnAddCredentialForSelectedAuthMode);
    } else if (FeatureFlags.oauthMultipleIdpOnServer && shouldShowAddCredentialIdpDialog(oauthConfigList)) {
      showAddCredentialIdpDialog(typeInfo.type, oauthConfigList, OnAddCredentialForSelectedIdp);
    } else if (oauthConfigList.length === 1 &&
      OAuthCredentialsService.shouldIgnoreDefaultOAuthConfigId(oauthConfigList[0])) {
      // If a connector doesn't support default idp, prompt an alert dialog directly.
      openReactAlertDialog({
        title: Messages.addNewSavedCredential_OAuthNotConfiguredTitle(),
        message: Messages.addNewSavedCredential_OAuthNotConfiguredMessage()
      });
    } else if (typeInfo.supportsCustomDomain === true || oauthConfigList[0]?.supportsCustomDomain){
      getOauthServerRoleAndRedirect(typeInfo.type, oauthConfigList[0]?.oauthConfigId);
    } else {
      await AddOAuthCredentialAction.attemptAuth(typeInfo.type);
      getCredentialsLists();
    }
  };

  const deleteCredential = async (oauthCredential: OAuthCredentialViewModel) => {
    await showDeleteOAuthCredentialsDialog(Set.fromIdItemArray([oauthCredential.credential]));
    getCredentialsLists();
  };

  const deleteAllCredentials = async () => {
    await showDeleteSavedPasswordActionDialog();
    getCredentialsLists();
  };

  return (
    <DeepLink deepLinkId={DEEPLINK_ID}>
      <table>
      <tbody data-tb-test-id="datasource-credential-section" className={UserSettingsStyle.tbody}>
          {/* Saved Credentials Section */}
          <tr>
            <td className={UserSettingsStyle.labelText}>
              <h4>{Messages.savedCredentials()}</h4>
            </td>
            <td>
              <div className={UserSettingsStyle.securityButton}>
                <ButtonWidget testId= "saved-credentials-delete-all"
                  buttonType={ ButtonType.Outline }
                  handleClick={ deleteAllCredentials }>
                  { Messages.deleteAllCredentialsAndPasswords_action() }
                </ButtonWidget>
              </div>
            </td>
          </tr>
          {credentialsLists.length > 0 &&
            <tr data-test-id="saved-credentials-section">
              <td></td>
              <td className={UserSettingsStyle.detailsValue} colSpan={2}>
                <div className="tb-oauth-table tb-enable-selection">
                {credentialsLists.map(credentialList =>
                  <div className="tb-oauth-type" key={credentialList.typeInfo.type}data-tb-test-id={'tb-credentials-' + credentialList.typeInfo.type + '-parent'}>
                    <div className="tb-oauth-type-header">
                      <span className="tb-oauth-type-title" data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-title'}>
                        {CredentialTypeFilter.filter(credentialList.typeInfo)}
                        <ConnectorInfoButton typeInfo={credentialList.typeInfo}/>
                      </span>
                      {credentialList.addEnabled &&
                        <a className="tb-oauth-type-add"
                        data-tb-test-id={'tb-credential-' + credentialList.typeInfo.type + '-add'}
                        href={AddOAuthCredentialAction.href(credentialList.typeInfo)}
                        onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {addCredential(credentialList.typeInfo); event.preventDefault();}}>
                          {Messages.add()}
                        </a>}
                    </div>
                    {credentialList.serviceAccountCredentials.length > 0 &&
                      <div className="tb-credential-type-header">
                        <span className="tb-credential-type-title">
                          {credentialList.typeInfo.type === 'snowflake' ? Messages.savedCredentials_KeyPairAuth() : Messages.savedCredentials_ServiceAccount()}
                        </span>
                      </div>
                    }
                    {credentialList.serviceAccountCredentials.map(credRow =>
                      <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                    )}
                    {credentialList.serviceAccountCredentials.length > 0 &&
                      <div className="tb-credential-type-header">
                        <span className="tb-credential-type-title">
                          {Messages.savedCredentials_OAuth()}
                        </span>
                      </div>
                    }
                    {Object.entries(credentialList.oauthConfigIdToCredMap).map(([key, creds]) =>
                      creds.length > 0 &&
                      <><div className="tb-credential-type-header">
                        <span className="tb-oauth-idp-title">
                          {credentialList.oauthConfigIdToLabel[key]}
                        </span>
                      </div>
                      {creds.map(credRow =>
                      <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                      )}</>
                    )}
                    {Object.keys(credentialList.oauthConfigIdToCredMap).length === 0 && credentialList.credentials.map(credRow =>
                      <SavedCredentialsTypeRow credRow={credRow} deleteCredential={deleteCredential} key={credRow.credential.id}/>
                    )}
                  </div>
                )}
                </div>
              </td>
            </tr>}
      </tbody>
      </table>
    </DeepLink>
  );
}


export type ConnectorInfoButtonProps = {
  typeInfo: IOAuthCredentialTypeInfo;
}

export function ConnectorInfoButton(props: ConnectorInfoButtonProps,   openDetail: () => void): JSX.Element {
  if (props.typeInfo.type === 'google-sheets') {
    return (<DeprecatedInfoButton testId={`${props.typeInfo.type}`} onClick={openDetail} pre_link={Messages.deprecate_gsheet_pre_link()} link={Messages.deprecate_gsheet_instruction_link()} title={Messages.deprecate_gsheet_title()} link_descriptor={Messages.deprecate_gsheet_link_descriptor()}/>
    );
  }
  else{
    return (<div></div>);
  }
}

export type SavedCredentialsTypeRowProps = {
  credRow: OAuthCredentialViewModel;
  deleteCredential: (oauthCredential: OAuthCredentialViewModel) => Promise<void>;
}

export function SavedCredentialsTypeRow(props: SavedCredentialsTypeRowProps): JSX.Element {
  const [isTestInProgress, setIsTestInProgress] = React.useState(false);
  const [isTestSuccessful, setIsTestSuccessful] = React.useState(undefined);
  const credRow = props.credRow;

  const testCredential = (oauthCredential: OAuthCredentialViewModel) => {
    setIsTestInProgress(true);
    const handleTestResult = (success: any) => {
      setIsTestSuccessful(!!success);
      setIsTestInProgress(false);
    };
    OAuthCredentials.test(oauthCredential.credential.id)
      .then(handleTestResult, () => handleTestResult(false));
  };

  return (
    <div className="tb-oauth-type-row">
      <div className="tb-oauth-type-row-details">
        <span className="tb-oauth-type-username" title={credRow.credential.username} data-tb-test-id={'tb-credential-' + credRow.credential.id + '-username'}>
          <span>{credRow.credential.username}<br></br></span>
          {credRow.credential.keypairAuthServer && <span>{credRow.credential.keypairAuthServer}<br></br></span>}
          {credRow.credential.instanceUrl && <span>{credRow.credential.instanceUrl}<br></br></span>}
          {credRow.credential.role && <span>{credRow.credential.role}<br></br></span>}
        </span>
        {/* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        <a className="tb-oauth-type-delete"
          data-tb-test-id={'tb-credential-' + credRow.credential.id + '-delete-button'}
          onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {props.deleteCredential(credRow); event.preventDefault();}}>
            {Messages.deleteMessage()}
        </a>}
        {credRow.test.enabled &&
        /* eslint-disable jsx-a11y/anchor-is-valid */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
          <a className="tb-oauth-type-test"
            data-tb-test-id={'tb-credential-' + credRow.credential.id + '-test-button'}
            onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {testCredential(credRow); event.preventDefault();}}>
              {Messages.test()}
          </a>}
      </div>
      <div className="tb-oauth-test-results">
        {isTestInProgress && <span className="tb-icon-spinner tb-lr-padded-narrow"></span>}
        {!isTestInProgress && isTestSuccessful === true &&
          <span className="tb-oauth-test-message" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-testing-message'}>
            {Messages.oauthCredentialTestSuccess()}
          </span>}
        {!isTestInProgress && isTestSuccessful === false &&
        <span className="tb-oauth-test-message tb-error" data-tb-test-id={'tb-credential-' + credRow.credential.id + '-error-message'}>
          {Messages.oauthCredentialTestError()}
        </span>}
      </div>
    </div>
  );
}

export const SavedCredentialSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.savedCredentials(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  isAdmin: false,
  tags: () => { return [Messages.userSettings_Security_Header()]; }
});